import React, { RefObject, useCallback, useEffect, useState } from 'react';

import { useNavigate } from 'react-router-dom';

import classNames from 'classnames';

import useBookingRootLink from '../../hooks/useBookingRootLink';
import useDeviceType from '../../providers/DeviceType/hooks';
import Container from '../Container';
import Button from '../ui/Button';
import ArrowLeftIcon from '../ui/icons/ArrowLeft';

import styles from './styles.module.scss';

type HeaderType = {
	scrollRef?: RefObject<HTMLElement | Window>;
	collapsed?: boolean;
	onGoBack?: () => void;
	title?: JSX.Element | string;
};

const Sizes = {
	scrollFromTop: 72,
};

const getOpacity = (scroll: number) => {
	if (scroll > Sizes.scrollFromTop) return 0;

	return Math.abs(scroll / Sizes.scrollFromTop - 1);
};

const getMove = (scroll: number) => {
	if (scroll > Sizes.scrollFromTop)
		return {
			transform: `translateY(0px)`,
			opacity: 1,
		};

	return {
		transform: `translateY(-${Sizes.scrollFromTop - scroll}px)`,
		opacity: Math.abs(getOpacity(scroll) - 1),
	};
};

const Header: React.FC<HeaderType> = ({ scrollRef, onGoBack, collapsed, title }) => {
	const { isDesktop } = useDeviceType();
	const navigate = useNavigate();
	const bookingRootLink = useBookingRootLink();
	const [scrollTop, setScrollTop] = useState(0);

	const handleScroll = useCallback((event: Event) => {
		const target = event.currentTarget as any;

		setScrollTop(target?.scrollTop || target?.scrollY);
	}, []);

	const handleGoBack = useCallback(() => {
		if (onGoBack) {
			onGoBack?.();
			return;
		}
		navigate(bookingRootLink);
	}, []);

	useEffect(() => {
		if (collapsed || !title) return;
		(scrollRef?.current ?? window)?.addEventListener('scroll', handleScroll);

		return () => {
			(scrollRef?.current ?? window)?.removeEventListener('scroll', handleScroll);
		};
	}, [title]);

	return (
		<>
			<header
				className={classNames([
					styles.header,
					isDesktop && styles.isDesktop,
					(collapsed || isDesktop) && styles.collapsed,
					scrollTop > Sizes.scrollFromTop && styles.withShadow,
				])}>
				<Container className={styles.container}>
					<div className={styles.inner}>
						<Button
							onClick={handleGoBack}
							size={'small'}
							style={{ width: 48 }}
							appearance={'primary'}>
							<ArrowLeftIcon />
						</Button>
						{!!title && (
							<div
								style={collapsed || isDesktop ? undefined : getMove(scrollTop)}
								className={styles.title}>
								{title}
							</div>
						)}
					</div>
				</Container>
			</header>
			{!collapsed && !isDesktop && (
				<div
					style={{
						width: '100%',
						marginTop: 64,
						backgroundColor: 'var(--background-alternative)',
						padding: '0 16px 16px',
					}}>
					{!!title && (
						<div style={{ opacity: getOpacity(scrollTop) }} className={styles.titleWithOpacity}>
							{title}
						</div>
					)}
				</div>
			)}
		</>
	);
};

export default Header;
